import * as React from 'react'
// libs
import { useMutation } from '@apollo/client/react'
// constants
import { Modals } from 'constants/index'
// context
import { PlanEditPageContext } from 'containers/Plan/Edit/layers/API'
// hooks
import { useCurrentUser, useShowModal } from 'hooks/index'
// graphql
import { REVIEW_DELETE_MUTATION } from 'graphql/reviews/mutations'
// types
import { SecurityProfileReviewPermission } from 'types/graphql'
// views
import { Wrapper } from 'components/Modals/PlanItems/components/ReviewsList/views'
// view components
import ScheduleReviewLink from 'components/Modals/CreateScheduledReviewModal/components/ScheduleReviewLink'
import ReviewCard, {
  IReviewCardActions
} from 'components/Modals/PlanItems/components/ReviewsList/components/ReviewCard'
import ConfirmDeleteModal from 'components/Modals/ConfirmDeleteModal'
// utils
import moment from 'moment'
import isFunction from 'lodash/isFunction'
import { handleError } from 'utils/errors/handlers'
import { filterVisibleReviews, generateReviews, getRefetchQueriesForReviews } from 'utils/reviews'

interface IIncomingProps {
  entity: any
  isSidebar?: boolean
  canCreate?: boolean
  canDelete?: boolean
  canModify?: boolean
  canViewCompleted?: SecurityProfileReviewPermission
  canViewInProgress?: SecurityProfileReviewPermission
}

const ReviewsList: React.FC<IIncomingProps> = ({
  entity,
  isSidebar,
  canCreate = false,
  canDelete = false,
  canModify = false,
  canViewCompleted = SecurityProfileReviewPermission.None,
  canViewInProgress = SecurityProfileReviewPermission.None
}) => {
  const [deleteReviewMutation] = useMutation(REVIEW_DELETE_MUTATION)

  const { treeQueryRefetch } = React.useContext(PlanEditPageContext)

  const currentUser = useCurrentUser()

  const { showModal: showReviewCreateModal } = useShowModal({ modalType: Modals.REVIEW_CREATE_MODAL })
  const { showModal: showReviewDetailsModal } = useShowModal({ modalType: Modals.REVIEW_DETAILS_MODAL })
  const { showModal: showSidebarReviewDetailsModal } = useShowModal({ modalType: Modals.SIDEBAR_REVIEW_DETAILS_MODAL })
  const { showModal: showScheduledReviewEditModal } = useShowModal({ modalType: Modals.SCHEDULE_REVIEW_MODAL })
  const { showModal: showConfirmDeleteModal } = useShowModal({ modalType: Modals.CONFIRM_DELETE_REVIEW_MODAL })

  const reviews = filterVisibleReviews(generateReviews({ entity }), {
    canViewInProgress,
    canViewCompleted,
    user: currentUser
  })

  // methods
  const actions: IReviewCardActions = {
    onStartReview: (review) => showReviewCreateModal({ entity, review }),
    onEditScheduledReview: (review) => showScheduledReviewEditModal({ isEdit: true, entity, review, canDelete }),
    onShowReviewDetails: (review) =>
      isSidebar ? showSidebarReviewDetailsModal({ entity, review }) : showReviewDetailsModal({ entity, review }),
    onDeleteReview: (reviewId) => {
      const refetchQueries = getRefetchQueriesForReviews(entity)

      showConfirmDeleteModal({
        width: 'sm',
        handleConfirm: async () => {
          try {
            if (isFunction(treeQueryRefetch)) {
              await treeQueryRefetch()
            }

            await deleteReviewMutation({
              variables: { id: reviewId },
              refetchQueries
            })
          } catch (errors) {
            handleError(errors, 'ReviewsList.onDeleteReview')
          }
        }
      })
    }
  }

  return (
    <Wrapper>
      {canCreate && <ScheduleReviewLink entity={entity} />}

      {reviews.map((review) => (
        <ReviewCard
          key={moment(review.startPeriod).toISOString()}
          review={review}
          entity={entity}
          actions={actions}
          isSidebar={isSidebar}
          canDelete={canDelete}
          canModify={canModify}
        />
      ))}

      <ConfirmDeleteModal header={'Delete Review Schedule'} modalType={Modals.CONFIRM_DELETE_SCHEDULED_REVIEW}>
        Are you sure you want to delete this review schedule?
      </ConfirmDeleteModal>
      <ConfirmDeleteModal header={'Delete Review'} modalType={Modals.CONFIRM_DELETE_REVIEW_MODAL}>
        Are you sure you want to delete this review?
      </ConfirmDeleteModal>
    </Wrapper>
  )
}

export default ReviewsList
